import * as React from 'react';
import { connect } from 'react-redux';
import { Grid, Col, Row } from 'react-flexbox-grid';
import { reduxForm } from 'redux-form';
import { compose, withHandlers, defaultProps, withStateHandlers, branch } from 'recompose';
import { get, noop, set } from 'lodash';
import parser from 'content-disposition';
import { saveAs } from 'file-saver';
import ExportIcon from 'material-ui/svg-icons/editor/insert-chart';
import { omit } from 'lodash';

import Dialog from '@inqool/uas-web-core/es/components/controls/Dialog';
import ButtonMenuRemote from '@inqool/uas-web-core/es/components/controls/ButtonMenuRemote';
import Button from '@inqool/uas-web-core/es/components/controls/Button';

import {
  Input,
  InputField,
  Date,
  DateField,
  AutoComplete,
  AutoCompleteField,
  RemoteAutoCompleteField,
  RemoteAutoComplete,
  RemoteChipInputField,
  RemoteChipInput,
  TimeField,
  Time,
  ChipInputField,
  ChipInput,
  Checkbox,
  CheckboxField
} from '@components/form';
import { withNotifications } from '@components/hoc/withNotifications';
import { fetchReport, getReportDetail } from '@components/exportButton/_exportButtonActions';
import {
  autocompleteFields,
  reportDataFields,
  validations,
  remoteAutocompleteFields,
  remoteAutocompleteMultipleFields
} from '@components/reports/_reportsEnums';
import { enhanceExportButton } from '@components/exportButton/enhancer';
import { ModalButton } from '@components/modalButton/ModalButton';
import { reportItemsTranformator } from '@utils/utils';
import { timeValidation } from '@utils/validators';
import { getConfigurationContext } from '@components/provider/configuration/ConfigurationProvider';

const extractFileName = headers => {
  const contentDisposition = headers.get('content-disposition');

  if (contentDisposition) {
    const disposition = parser.parse(contentDisposition);
    return get(disposition, 'parameters.filename');
  } else {
    return null;
  }
};

const ReportField = ({
  name,
  label,
  reduxData,
  hidden,
  report,
  validation,
  type,
  active,
  items: fieldItems,
  context,
  ...fieldAttributes
}) => {
  if (!active || hidden) {
    return null;
  }

  const { labelFunction, items, valueFunction = o => get(o, 'id') } = get(autocompleteFields, name, {});
  const remoteAutoCompleteAttributes = get(remoteAutocompleteFields, name, {});
  const remoteAutoCompleteMultipleAttributes: any = get(remoteAutocompleteMultipleFields, name, {});
  const sps = get(context, `config.extensions.sps`, false);
  if (sps && name === 'proceedingNumber') {
    type = 'remote_autocomplete';
    fieldAttributes = {
      ...fieldAttributes,
      params: { filter: [{ field: 'internalId', operation: 'EQ', value: get(reduxData, 'id') }] }
    };
  }

  const fieldName = report ? name : `data.${name}`;

  let validationArray: Array<any> = [];

  for (const val in validation) {
    if (validations[val]) {
      validationArray = [...validationArray, validations[val]];
    }
  }

  const fieldLabel = `${label}${get(validation, 'required') ? ' *' : ''}`;
  const switchField = fieldType => {
    switch (fieldType) {
      case 'string':
        return (
          <InputField
            component={Input}
            name={fieldName}
            multiLine={false}
            validate={validationArray}
            {...fieldAttributes}
          />
        );
      case 'time':
        return (
          <TimeField
            component={Time}
            name={fieldName}
            validate={[timeValidation, ...validationArray]}
            {...fieldAttributes}
          />
        );
      case 'datetime':
        return (
          <DateField
            component={Date}
            name={fieldName}
            validate={validationArray}
            dateOnly={false}
            allowFuture={true}
            {...fieldAttributes}
          />
        );
      case 'date':
        return (
          <DateField
            component={Date}
            name={fieldName}
            validate={validationArray}
            allowFuture={true}
            {...fieldAttributes}
          />
        );
      case 'autocomplete':
        return (
          <AutoCompleteField
            component={AutoComplete}
            name={fieldName}
            items={reduxData[items]}
            fullWidth={true}
            labelFunction={labelFunction}
            valueFunction={valueFunction}
            validate={validationArray}
            {...fieldAttributes}
          />
        );
      case 'radio':
        return (
          <AutoCompleteField
            component={AutoComplete}
            name={fieldName}
            items={reportItemsTranformator(fieldItems)}
            fullWidth={true}
            labelFunction={({ label: itemLabel }) => itemLabel}
            valueFunction={({ value: itemValue }) => itemValue}
            validate={validationArray}
          />
        );
      case 'select':
        return (
          <AutoCompleteField
            component={AutoComplete}
            name={fieldName}
            items={reportItemsTranformator(fieldItems)}
            fullWidth={true}
            labelFunction={({ label: itemLabel }) => itemLabel}
            valueFunction={({ value: itemValue }) => itemValue}
            validate={validationArray}
          />
        );
      case 'remote_autocomplete':
        return (
          <RemoteAutoCompleteField
            component={RemoteAutoComplete}
            name={fieldName}
            fullWidth={true}
            validate={validationArray}
            labelFunction={o => get(o, 'name')}
            valueFunction={o => get(o, 'id')}
            filterActive={fieldName === 'stationId'}
            {...fieldAttributes}
            {...remoteAutoCompleteAttributes}
          />
        );
      case 'autocomplete_multiple':
        return (
          <ChipInputField
            component={ChipInput}
            name={fieldName}
            items={reduxData[items]}
            labelFunction={labelFunction}
            valueFunction={valueFunction}
            validate={validationArray}
            {...fieldAttributes}
          />
        );
      case 'remote_autocomplete_multiple':
        return (
          <>
            <RemoteChipInputField
              component={RemoteChipInput}
              name={fieldName}
              fullWidth={true}
              validate={validationArray}
              {...fieldAttributes}
              {...remoteAutoCompleteMultipleAttributes}
            />
            {remoteAutoCompleteMultipleAttributes.modalButtonProps && (
              <ModalButton {...remoteAutoCompleteMultipleAttributes.modalButtonProps} />
            )}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Row>
      <Col md={4}>
        <div style={{ marginTop: 10 }}>{fieldLabel}</div>
      </Col>
      <Col md={8}>{switchField(type)}</Col>
    </Row>
  );
};

const FileNameDialogBase: React.SFC<any> = ({
  dialogOpened,
  closeDialog,
  modalFields,
  report,
  handleSubmit,
  reduxData,
  afterSubmit,
  location
}) => {
  const context = getConfigurationContext();

  return (
    <Dialog open={dialogOpened} onRequestClose={closeDialog} autoScrollBodyContent={true}>
      <form onSubmit={handleSubmit}>
        <Grid fluid={true}>
          <Row>
            <Col md={12}>
              <h2 style={{ marginTop: 0 }}>{get(report, 'name', '')}</h2>
            </Col>
          </Row>
          {modalFields.map(f => <ReportField key={f.name} {...f} context={context} reduxData={reduxData} />)}
          {afterSubmit &&
            location === 'eventDetail' && (
              <CheckboxField
                component={Checkbox}
                id="generateToEvent"
                name="generateToEvent"
                label="Přidat soubor k události"
              />
            )}

          <div className="buttons" style={{ marginTop: 20 }}>
            <Button label="Ok" type="submit" primary={true} style={{ marginRight: 10 }} />
            <Button label="Cancel" onClick={closeDialog} />
          </div>
        </Grid>
      </form>
    </Dialog>
  );
};

const ExportButtonBase = ({ exportLabel, location, handleItemClick, urlPrefix, disabled, ...rest }) => (
  <div style={{ marginLeft: 5 }}>
    <ButtonMenuRemote
      icon={<ExportIcon />}
      label={exportLabel}
      url={`${urlPrefix}/api/reports/public?location=${location}`}
      onItemClick={handleItemClick}
      labelFunction={({ label }) => label}
      disabled={disabled}
    />
    <FileNameDialogBase {...rest} location={location} />
  </div>
);

const mapStateToProps = (state, { formName }) => {
  if (!formName) {
    console.error('formName in ExportButton not defined!');
    return;
  }

  return {
    reduxData: get(state, `${formName}.values`, {})
  };
};

const ExportButton = compose<any, any>(
  defaultProps({
    input: {},
    loaderFunction: noop,
    urlPrefix: '',
    defaultData: {},
    afterSubmit: noop
  }),
  connect(mapStateToProps),
  withNotifications({ fetchReport, getReportDetail }),
  withStateHandlers<any, any>(
    {
      dialogOpened: false,
      report: null,
      reportData: null,
      modalFields: [],
      initialValues: {}
    },
    {
      setState: oldState => newState => ({ ...oldState, ...newState })
    }
  ),
  withHandlers({
    openDialog: ({ loaderFunction, setState }) => async (report, reportData, reportDetail) => {
      loaderFunction(true);

      const modalFields = reportDetail.fields.map(({ name, code, ...rest }) => ({
        label: name,
        name: code,
        ...rest
      }));
      const initialValues = reportDetail.fields.reduce(
        (acc, { code, report: fieldReportFlag, default: defaultValue }) => {
          if (defaultValue === undefined) {
            return acc;
          }

          const fieldName = fieldReportFlag ? code : `data.${code}`;
          return set({ ...acc }, fieldName, defaultValue);
        },
        {}
      );

      setState({ dialogOpened: true, report, reportData, modalFields, initialValues });
      loaderFunction(false);
    },
    closeDialog: ({ setState }) => () =>
      setState({ dialogOpened: false, report: null, reportData: null, initialValues: {} }),
    onSubmitDialog: ({ loaderFunction, fetchReportNotify, afterSubmit, eventId }) => async (report, data) => {
      const { generateToEvent } = data;
      data = omit(data, 'generateToEvent');
      if (afterSubmit && generateToEvent) {
        afterSubmit(eventId, report, data);
      } else {
        const { id } = report;
        loaderFunction(true);
        const { blob, headers } = await fetchReportNotify(id, data);

        if (blob) {
          const fileName = extractFileName(headers);
          saveAs(blob, fileName ? fileName : report.fileName);
        }
        loaderFunction(false);
      }
    }
  }),
  withHandlers({
    onSubmit: ({
      loaderFunction,
      closeDialog,
      report,
      reportData,
      modalFields,
      reduxData,
      defaultData,
      onSubmitDialog
    }) => async ({ data, ...reportModalData }) => {
      loaderFunction(true);
      const additionalReportData = modalFields
        .filter(({ active }) => !active)
        .reduce((acc, { name }) => ({ ...acc, [name]: reduxData[reportDataFields[name]] }), {});

      await onSubmitDialog(report, {
        ...defaultData,
        ...reportData,
        ...reportModalData,
        ...additionalReportData,
        data
      });
      loaderFunction(false);
      closeDialog();
    },

    handleItemClick: ({ loaderFunction, input, openDialog, onSubmitDialog, getReportDetailNotify }) => async report => {
      let data;
      loaderFunction(true);
      if (input instanceof Function) {
        const mayBePromise = input(report);
        data = mayBePromise instanceof Promise ? await mayBePromise : mayBePromise;
      } else {
        data = input;
      }

      const reportDetail = await getReportDetailNotify(get(report, 'id'));

      if (reportDetail && reportDetail.fields && reportDetail.fields.length) {
        await openDialog(report, data, reportDetail);
      } else {
        await onSubmitDialog(report, data);
      }
      loaderFunction(false);
    }
  }),
  branch(({ dialogOpened }) => !!dialogOpened, reduxForm({ form: 'exportButton-dialog' }))
)(ExportButtonBase);

export const EnhancedExportButton = enhanceExportButton(ExportButton);

export default ExportButton;



// WEBPACK FOOTER //
// ./src/components/exportButton/ExportButton.tsx